import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { MarkerCategory } from "../types/domain.types";

/**
 * Cola offline para marcadores pendientes de enviar al backend.
 * Usamos IndexedDB via `idb`.
 */
export interface PendingMarker {
  localId: string; // uuid cliente (key)
  planId: string;
  qrCode: string;
  category: MarkerCategory | null;
  coordX: number;
  coordY: number;
  comment: string | null;
  createdAtClient: string; // ISO
  userId: string;
  userEmail: string;
  retries: number;
}

interface IMDatabase extends DBSchema {
  "pending-markers": {
    key: string;
    value: PendingMarker;
    indexes: { "by-plan": string };
  };
}

const DB_NAME = "industrialmap-db";
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<IMDatabase>> | null = null;

function getDb(): Promise<IDBPDatabase<IMDatabase>> {
  if (!dbPromise) {
    dbPromise = openDB<IMDatabase>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("pending-markers")) {
          const store = db.createObjectStore("pending-markers", {
            keyPath: "localId",
          });
          store.createIndex("by-plan", "planId");
        }
      },
    });
  }
  return dbPromise;
}

export async function addPendingMarker(m: PendingMarker): Promise<void> {
  const db = await getDb();
  await db.put("pending-markers", m);
}

export async function listPendingMarkers(
  planId?: string,
): Promise<PendingMarker[]> {
  const db = await getDb();
  if (planId) {
    return db.getAllFromIndex("pending-markers", "by-plan", planId);
  }
  return db.getAll("pending-markers");
}

export async function removePendingMarker(localId: string): Promise<void> {
  const db = await getDb();
  await db.delete("pending-markers", localId);
}

export async function incrementRetries(localId: string): Promise<void> {
  const db = await getDb();
  const current = await db.get("pending-markers", localId);
  if (!current) return;
  current.retries += 1;
  await db.put("pending-markers", current);
}
